import {
  createContext,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Session state - in-memory + `sessionStorage`, cleared on tab close, no silent refresh rotation yet.
 *
 * The platform has no second factor (decision of 8 October 2026): a correct password opens a session, and what that session may do is
 * decided by the server on every request. This file therefore knows nothing about a challenge - only about WHO is signed in, so the
 * screens can show the entries that make sense (`roles`, read from the token and used for display only).
 *
 * `/api/v1/admin/*` is structurally without a shop, so there is no `activeShopId` here at all.
 */
export interface SessionState {
  readonly accessToken: string | null;
  readonly refreshToken: string | null;
  /**
   * The password that opened this session is a PROVISIONAL one: the API refuses everything but the password change (the second
   * factor included) until the person has chosen their own. Kept only so the screen can send them straight there; the server decides
   * again on every call.
   */
  readonly mustChangePassword: boolean;
}

interface SessionContextValue extends SessionState {
  readonly isAuthenticated: boolean;
  /**
   * The platform roles named in the access token (`superadmin`, `platform_admin`). A DISPLAY hint only - it decides which menu entries
   * and buttons are offered, never what is allowed: the server resolves the roles from the database on every request.
   */
  readonly roles: readonly string[];
  /**
   * The account id named in the access token (`sub`). A DISPLAY hint only, like `roles`: it lets a screen mark "you" and hide the
   * actions that would be refused on one's own account; the server refuses them anyway.
   */
  readonly userId: string | null;
  setSession: (next: {
    accessToken: string;
    refreshToken: string;
    mustChangePassword?: boolean;
  }) => void;
  clearSession: () => void;
}

const STORAGE_KEY = 'souk-admin-session';

const EMPTY_SESSION: SessionState = {
  accessToken: null,
  refreshToken: null,
  mustChangePassword: false,
};

function readStored(): SessionState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SESSION;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && 'accessToken' in parsed) {
      const candidate = parsed as Partial<SessionState>;
      return {
        accessToken: typeof candidate.accessToken === 'string' ? candidate.accessToken : null,
        refreshToken: typeof candidate.refreshToken === 'string' ? candidate.refreshToken : null,
        mustChangePassword: candidate.mustChangePassword === true,
      };
    }
  } catch {
    // Storage disabled or corrupted — degrade to "signed out", never throw.
  }
  return EMPTY_SESSION;
}

function writeStored(state: SessionState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Session then survives for this render only — acceptable degradation.
  }
}

/**
 * DT-4 / P1-2 — purges the stored session from OUTSIDE React (`api-client.ts`
 * is a plain fetch wrapper, not a hook, and cannot call `clearSession()` on
 * `SessionProvider`'s own state). Writes the SAME empty shape `clearSession()`
 * writes, through the SAME `writeStored()`, so there is exactly one "what an
 * empty session looks like" in this file. The in-memory React state is left
 * stale on purpose: the caller (`api-client.ts`) always follows this with a
 * hard `window.location` redirect to `/login`, which remounts the whole app
 * and re-reads this now-empty value via `useState(readStored)` — the same
 * one guarantee a soft `navigate()` could not make without also threading
 * `clearSession()` through the plain fetch layer.
 */
export function clearStoredSession(): void {
  writeStored(EMPTY_SESSION);
}

/**
 * The API answered `PASSWORD_CHANGE_REQUIRED`: this session's password is a provisional one (the flag was lost - a reload, another tab,
 * a token minted before the password was reset). Recorded from OUTSIDE React, like `clearStoredSession`; the caller then redirects to
 * the change screen, which remounts the app and re-reads this.
 */
export function markPasswordChangeRequired(): void {
  writeStored({ ...readStored(), mustChangePassword: true });
}

/**
 * Reads the `roles` claim of an access token WITHOUT verifying it (the browser has no key, and needs none): the result only ever
 * shapes the screen. A forged value could at worst show a button the server then refuses.
 */
export function rolesOfToken(token: string | null): readonly string[] {
  if (token === null) return [];
  try {
    const payload = token.split('.')[1];
    if (payload === undefined) return [];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims: unknown = JSON.parse(json);
    if (typeof claims !== 'object' || claims === null || !('roles' in claims)) return [];
    const roles = claims.roles;
    return Array.isArray(roles)
      ? roles.filter((role): role is string => typeof role === 'string')
      : [];
  } catch {
    return [];
  }
}

/** Reads the `sub` claim of an access token WITHOUT verifying it (display only, like `rolesOfToken`). */
export function userIdOfToken(token: string | null): string | null {
  if (token === null) return null;
  try {
    const payload = token.split('.')[1];
    if (payload === undefined) return null;
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof claims !== 'object' || claims === null || !('sub' in claims)) return null;
    return typeof claims.sub === 'string' ? claims.sub : null;
  } catch {
    return null;
  }
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, setState] = useState<SessionState>(readStored);

  /**
   * A fresh sign-in never inherits anything from a PREVIOUS session in the same tab: it replaces the whole state.
   */
  const setSession = useCallback(
    (next: { accessToken: string; refreshToken: string; mustChangePassword?: boolean }) => {
      setState(() => {
        const merged: SessionState = {
          accessToken: next.accessToken,
          refreshToken: next.refreshToken,
          mustChangePassword: next.mustChangePassword === true,
        };
        writeStored(merged);
        return merged;
      });
    },
    [],
  );

  const clearSession = useCallback(() => {
    writeStored(EMPTY_SESSION);
    setState(EMPTY_SESSION);
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({
      ...state,
      isAuthenticated: state.accessToken !== null,
      roles: rolesOfToken(state.accessToken),
      userId: userIdOfToken(state.accessToken),
      setSession,
      clearSession,
    }),
    [state, setSession, clearSession],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider.');
  return ctx;
}
